import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { cn } from "@arcadeai/design-system/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";
import { useServerAuthStatus } from "@/app/servers/api/hooks";
import { OAuthStatusBadge } from "@/app/servers/components/oauth/oauth-status-badge";
import { requiresOAuth, type Server } from "@/app/servers/types";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getBindingInfo } from "@/lib/binding";
import { HealthIndicatorColumn } from "./health-indicator-column";
import { ServerActions } from "./server-actions";

const ServerStatusCell = ({ server }: { server: Server }) => {
  const isEnabled = server.enabled;
  const needsOAuth = requiresOAuth(server);
  const { isAuthorized } = useServerAuthStatus(
    server.id,
    server.binding.type,
    server.type
  );
  const showAuthBadge = needsOAuth && !isAuthorized;

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold text-xs transition-colors",
          isEnabled
            ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800"
            : "border-destructive bg-destructive text-white hover:bg-destructive/90 active:bg-destructive/80"
        )}
      >
        {isEnabled ? "Active" : "Disabled"}
      </div>
      <OAuthStatusBadge requiresAuth={showAuthBadge} />
      <HealthIndicatorColumn server={server} />
    </div>
  );
};

export const serverColumns: ColumnDef<Server>[] = [
  {
    id: "id",
    accessorKey: "id",
    meta: {
      displayName: "ID",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => <span>{row.original.id}</span>,
  },
  {
    id: "uri",
    accessorFn: (row) => (row.type === "http" ? row.http?.uri : row.mcp?.uri),
    meta: {
      displayName: "URI",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="URI" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const uri = row.getValue("uri") as string | undefined;
      const isStatic = row.original.binding.type === "static";
      const isManaged = row.original.managed;

      if (!uri) {
        return null;
      }

      if (isStatic || isManaged) {
        return (
          <div className="flex w-56 items-center gap-2">
            <span className="text-muted-foreground">Arcade Hosted</span>
          </div>
        );
      }

      return (
        <div className="flex w-56 items-center gap-2">
          <span className="truncate">{uri}</span>
          <CopyButton className="h-5 w-5" content={uri} variant="ghost" />
        </div>
      );
    },
  },
  {
    id: "type",
    accessorFn: (row) => {
      const types: string[] = [];
      if (row.type === "http") {
        types.push("http");
      }
      if (row.type === "mcp") {
        types.push("mcp");
      }
      types.push(row.binding.type);
      return types;
    },
    meta: {
      displayName: "Type",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const bindingType = row.original.binding.type;
      const bindingInfo = getBindingInfo(bindingType);
      const { background, hoverBackground, ring, badgeText, badgeBorder } =
        bindingInfo.classes;

      const className = `${background} ${hoverBackground} ${ring} ${badgeText} ${badgeBorder}`;

      return (
        <div className="flex items-center gap-2">
          {row.original.type === "http" && (
            <Badge className="bg-sky-500 text-white">HTTP</Badge>
          )}
          {row.original.type === "mcp" && (
            <Badge className="bg-slate-500 text-white">MCP</Badge>
          )}
          <Badge className={className} variant="secondary">
            {bindingInfo.displayName}
          </Badge>
        </div>
      );
    },
    filterFn: (row, _, filterValue) => {
      if (filterValue.length === 0) {
        return true;
      }
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const types = row.getValue("type") as string[];
      return filterValue.some((val: string) => types.includes(val));
    },
  },
  {
    id: "timeout",
    accessorFn: (row) =>
      row.type === "http" ? row.http?.timeout : row.mcp?.timeout,
    meta: {
      displayName: "Timeout",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Timeout" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const timeout = row.getValue("timeout") as number | undefined;
      return <span>{timeout}s</span>;
    },
  },
  {
    id: "retry",
    accessorFn: (row) =>
      row.type === "http" ? row.http?.retry : row.mcp?.retry,
    meta: {
      displayName: "Retries",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Retries" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const retry = row.getValue("retry") as number | undefined;
      return <span>{retry}</span>;
    },
  },
  {
    id: "enabled",
    accessorKey: "enabled",
    meta: {
      displayName: "Status",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <ServerStatusCell server={row.original} />,
    filterFn: (row, _, filterValue) => {
      if (filterValue.length === 0) {
        return true;
      }
      return filterValue
        .map((v: string) => v === "true")
        .includes(row.original.enabled);
    },
  },
  {
    id: "actions",
    enableHiding: false,
    meta: {
      displayName: "Actions",
      defaultVisible: true,
    },
    header: () => <div className="flex items-center space-x-2">Actions</div>,
    cell: ({ row }) => <ServerActions server={row.original} />,
  },
];
